import { useState, useMemo } from "react";
import { useQuery } from "convex/react";
import { User, Search, X } from "lucide-react";
import { UpdateMarkdown, updateProseClasses } from "@/components/UpdateMarkdown";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/convex";
import { capture } from "@/lib/analytics/posthog";

function splitMarkdownPreview(content: string) {
  // Show only the first line (heading) as preview — button appears right after
  const firstNewline = content.indexOf("\n");
  if (firstNewline === -1) {
    return { preview: content, rest: null };
  }

  const preview = content.slice(0, firstNewline).trim();
  const rest = content.slice(firstNewline + 1).trim();
  return { preview, rest: rest || null };
}

const Feed = () => {
  const updates = useQuery(api.updates.list, {});
  const [expandedUpdates, setExpandedUpdates] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  const filteredUpdates = useMemo(() => {
    if (!updates) return undefined;
    if (!searchQuery.trim()) return updates;
    const q = searchQuery.toLowerCase();
    return updates.filter((u) => {
      const title = (u.title || "").toLowerCase();
      const excerpt = (u.excerpt || "").toLowerCase();
      const author = (u.author || "").toLowerCase();
      const category = (u.category || "").toLowerCase();
      const content = (u.content || "").toLowerCase();
      return title.includes(q) || excerpt.includes(q) || author.includes(q) || category.includes(q) || content.includes(q);
    });
  }, [updates, searchQuery]);

  const toggleUpdate = (update: { _id: string; title?: string; category?: string }) => {
    const id = update._id;
    const newExpanded = new Set(expandedUpdates);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
      // Activation event for the Member Portal funnel (Members have no chat, so
      // opening a briefing summary is the equivalent "first app action"). Title +
      // category are published content, not private member data — safe to capture.
      capture("briefing_opened", {
        update_id: id,
        title: update.title,
        category: update.category,
      });
    }
    setExpandedUpdates(newExpanded);
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-6 py-10 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-semibold text-foreground mb-2 tracking-tight">
            Updates from Energy Talking Points
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base tracking-wide">
            Latest insights from our monthly ETP member and bi-weekly ETP Action Leaders calls.
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative flex items-center mb-8">
          <Search className="absolute left-3 h-[1.125rem] w-[1.125rem] text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Search updates and recordings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full sm:w-96 pl-10 pr-9 py-2 bg-transparent border-0 border-b border-border text-base text-foreground placeholder:text-muted-foreground outline-none ring-0 focus:ring-0 focus:outline-none transition-colors duration-200 ease-out focus:border-primary/50 rounded-none"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 text-muted-foreground/60 hover:text-foreground transition-colors [min-height:unset]"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Loading State */}
        {updates === undefined && (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-card border-border/40">
                <CardContent className="p-5 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-5 w-28" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Updates List */}
        {filteredUpdates && (
          <div className="space-y-6">
            {filteredUpdates.length === 0 && searchQuery && (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No updates matching "{searchQuery}"</p>
              </div>
            )}
            {filteredUpdates.map((update) => {
              const isExpanded = expandedUpdates.has(update._id);
              const content = update.content || "";
              const { preview, rest } = splitMarkdownPreview(content);

              return (
                <Card key={update._id} className="bg-card border-border/40">
                  <CardContent className="p-5">
                    {/* Title */}
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      {update.title}
                    </h3>

                    {/* Meta row: badges */}
                    <div className="flex items-center gap-2 flex-wrap mb-4">
                      <Badge variant="outline" className="font-medium border-blue-900/40 text-blue-900">
                        {update.category}
                      </Badge>
                      <Badge variant="outline" className="font-normal border-foreground/20 text-foreground/80">
                        <User className="w-3 h-3 mr-1" />
                        {update.author}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {update.date}
                      </span>
                    </div>

                    {/* Excerpt */}
                    <p className="text-sm text-foreground/70 italic mb-4 leading-relaxed">
                      {update.excerpt}
                    </p>

                    <hr className="border-t border-border/60 mb-4" />

                    {/* Video Recording */}
                    {update.vimeoId && (
                      <div className="mb-4 w-full sm:w-[60%] mx-auto rounded-lg overflow-hidden bg-black aspect-video">
                        <iframe
                          src={`https://player.vimeo.com/video/${update.vimeoId}`}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                          allowFullScreen
                          loading="lazy"
                          title={update.title}
                        />
                      </div>
                    )}

                    {/* Markdown Content */}
                    {content && (
                      <Collapsible open={isExpanded} onOpenChange={() => toggleUpdate(update)}>
                        <CollapsibleContent>
                          <div className={updateProseClasses}>
                            <UpdateMarkdown>{content}</UpdateMarkdown>
                          </div>
                        </CollapsibleContent>

                        <CollapsibleTrigger asChild>
                          <Button variant="outline" size="sm" className="mt-3">
                            {isExpanded ? "Hide summary" : "Read Alex's summary"}
                          </Button>
                        </CollapsibleTrigger>
                      </Collapsible>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Feed;
